/**
 * SaveButton Component
 *
 * Manual save button for persisting workspace to localStorage.
 * Shows success/error feedback and storage capacity warnings.
 *
 * Features:
 * - Disabled when no unsaved changes
 * - Success toast on successful save
 * - Error toast with message on failure
 * - Storage warning banner when approaching limit
 */

import { useState, useEffect, useCallback } from 'react';
import { Save, Check, AlertTriangle, X } from 'lucide-react';
import { useSceneStore } from '../stores/sceneStore';
import {
  saveWorkspace,
  isStorageNearCapacity,
} from '../utils/workspacePersistence';

/**
 * Toast message type for feedback display
 */
interface ToastMessage {
  type: 'success' | 'error';
  message: string;
}

export function SaveButton() {
  const isDirty = useSceneStore((state) => state.isDirty);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [showStorageWarning, setShowStorageWarning] = useState(() =>
    isStorageNearCapacity()
  );
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Handle save button click
   * Saves workspace to localStorage and shows feedback
   */
  const handleSave = useCallback(() => {
    if (!isDirty || isSaving) return;

    setIsSaving(true);

    // Small delay to show saving state for UX
    setTimeout(() => {
      const result = saveWorkspace();

      if (result.success) {
        setToast({
          type: 'success',
          message: 'Workspace saved successfully',
        });
      } else {
        setToast({
          type: 'error',
          message: result.error || 'Failed to save workspace',
        });
      }

      // Update storage warning status
      setShowStorageWarning(isStorageNearCapacity());
      setIsSaving(false);
    }, 100);
  }, [isDirty, isSaving]);

  // Auto-dismiss success toast after 3 seconds
  useEffect(() => {
    if (toast?.type === 'success') {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Auto-dismiss error toast after 5 seconds
  useEffect(() => {
    if (toast?.type === 'error') {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const isDisabled = !isDirty;
  const tooltip = isDisabled
    ? 'No unsaved changes'
    : 'Save workspace to browser';

  return (
    <div className="flex items-center gap-2">
      {/* Storage Warning Banner */}
      {showStorageWarning && (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
          <AlertTriangle className="w-4 h-4 text-yellow-400" />
          <span className="text-sm text-yellow-400">
            Browser storage is almost full
          </span>
          <button
            onClick={() => setShowStorageWarning(false)}
            className="p-0.5 hover:bg-yellow-500/20 rounded"
            aria-label="Dismiss warning"
          >
            <X className="w-3 h-3 text-yellow-400" />
          </button>
        </div>
      )}

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={isDisabled}
        className={`
          flex items-center gap-2 px-3 py-1.5 rounded-lg
          text-sm font-medium transition-colors
          ${
            isDisabled
              ? 'bg-dark-border/50 text-dark-muted cursor-not-allowed opacity-50'
              : 'bg-dark-border hover:bg-dark-border/80 text-dark-text'
          }
        `}
        title={tooltip}
      >
        {isSaving ? (
          <>
            <div className="w-4 h-4 border-2 border-dark-muted border-t-transparent rounded-full animate-spin" />
            <span>Saving...</span>
          </>
        ) : (
          <>
            <Save className="w-4 h-4" />
            <span>Save</span>
          </>
        )}
      </button>

      {/* Toast Messages */}
      {toast && (
        <div
          className={`
            flex items-center gap-2 px-3 py-1.5 rounded-lg border
            ${
              toast.type === 'success'
                ? 'bg-green-500/10 border-green-500/20'
                : 'bg-red-500/10 border-red-500/20'
            }
          `}
        >
          {toast.type === 'success' ? (
            <Check className="w-4 h-4 text-green-400" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-red-400" />
          )}
          <span
            className={`text-sm ${
              toast.type === 'success' ? 'text-green-400' : 'text-red-400'
            }`}
          >
            {toast.message}
          </span>
          <button
            onClick={() => setToast(null)}
            className={`p-0.5 rounded ${
              toast.type === 'success'
                ? 'hover:bg-green-500/20'
                : 'hover:bg-red-500/20'
            }`}
            aria-label="Dismiss"
          >
            <X
              className={`w-3 h-3 ${
                toast.type === 'success' ? 'text-green-400' : 'text-red-400'
              }`}
            />
          </button>
        </div>
      )}
    </div>
  );
}
